import { apiClient } from "@/http";
import type { EpisodeStatus, EpisodeTerm } from "@/modules/dataset/public";
import { apiRoutes } from "./apiRoutes";
import type { HttpMethod } from "./common";
import type { MemberRef } from "./member";
import type {
  EpisodeInvalidReason,
  EpisodeSnapshot,
  EpisodeValidationStatus,
} from "./episodeReviewState";
export type { EpisodeValidationStatus } from "./episodeReviewState";
import type { PageQuery, PageResp } from "./page";

/**
 * Backend는 raw Episode / EpisodeFile page를 내려준다. 이 모듈이 화면에서 쓰는
 * EpisodeSummary / EpisodeFile 형태로 변환하는 projection layer를 소유한다.
 */
// 백엔드 PageQuery 상한(api/pagination.py PAGE_SIZE_MAX=100)과 같은 값이다. 한 에피소드의
// 파일 수는 보통 카메라 수 정도라서 한 페이지로 충분하다. 200을 보내면 서버의 size>100 검증이
// 422를 반환한다.
const DEFAULT_EPISODE_FILE_PAGE_SIZE = 100;

type JsonObject = Record<string, unknown>;

export type EpisodeResp = {
  validation_status: EpisodeValidationStatus;
  // 무효 사유다. 목록 응답과 단건 조회(`EpisodeDetailResp`)가 모두 싣는다. 사유가 없으면 null 이다.
  invalid_reason?: EpisodeInvalidReason | null;
  accepted_invalid: boolean;
  accepted_invalid_snapshot?: EpisodeSnapshot | null;
  accepted_invalid_stale?: boolean | null;
  review_comment_snapshot?: EpisodeSnapshot | null;
  review_comment_stale?: boolean | null;
  uuid: string;
  artifact_source_uuid: string;
  name: string;
  // 0-based 디렉터리 순서다. BE 가 모든 insert 경로에서 채우는 non-null 계약이다(EpisodeResp).
  sequence_index: number;
  duration_seconds: number | null;
  recorded_at: string | null;
  status: EpisodeStatus;
  notes: string | null;
  metadata: JsonObject;
  created_at: string;
  modified_at: string;
  // annotation 낙관적 동시성 토큰이다(modified_at 의 opaque 직렬화). 편집할 때 이 값을
  // `expected_annotation_version` 으로 보낸다. 파싱·비교·정렬하지 않는 opaque string 이다.
  annotation_version: string;
  // 실패 여부다. true 는 실패, false 는 기본 성공이다. 새 에피소드는 false 로 시작한다.
  // 그래서 false 는 사람이 성공을 확인했다는 뜻이 아니다. 검토 완료 여부(`reviewed_at`)와 독립이다.
  task_failed: boolean;
  // 에피소드 전역 리뷰다(인라인 조회). 세 값은 아래처럼 파생한다(episodeReview.ts 헬퍼). 세 값은 직교한다.
  //   - 제외: 위 `status`(=excluded)
  //   - 결과: `task_failed`
  //   - 결함 유무: `quality_tags` 가 비어 있지 않음
  quality_tags?: string[];
  // 정규화 전 자유 어휘다. 닫힌 어휘 축과 달리 값이 구조체다. 표기가 같아도 위치가 다르면
  // 다른 어휘다. 온톨로지에 연결했는지와 누가 붙였는지도 함께 본다.
  terms?: EpisodeTerm[];
  review_comment?: string | null;
  // 마지막으로 검토를 완료한 사람이다(uuid + 표시명). 미검토이거나 완료가 해제되었으면 null 이다.
  // member 가 삭제되어도 null 이다. FE 는 별도 멤버 조회 없이 이 이름을 표시한다.
  reviewed_by_member?: MemberRef | null;
  reviewed_at?: string | null;
  // 이 에피소드의 카메라 스트림 수와 채널명이다(사전순). 목록의 에피소드별 "N ch" 배지에 쓴다.
  camera_stream_count?: number;
  camera_stream_keys?: string[];
  // 이 데이터셋 안에서의 기준 밴드 이탈이다. 진단 전이거나 밴드가 없는 컬렉션이면 null 이다.
  band?: EpisodeBandDeviation | null;
};

/**
 * 기준 밴드에서 벗어난 정도다. action 과 observation 두 축이 있다.
 *
 * **이 데이터셋 안에서만 뜻이 있다.** 같은 에피소드라도 어떤 집합에 속하는지에 따라 밴드가
 * 달라진다. 그래서 다른 데이터셋과 비교할 수 없다.
 *
 * 축 값은 점 x dim 가중이다. 그래서 1-dim 그리퍼가 모두 벗어나도 7-dim pose 와 합치면 값이
 * 작아진다. "무엇이 크게 벗어났나"는 축별 최악 채널에서만 알 수 있다.
 *
 * 측정할 수 없었던 축은 null 이다. **0% 가 아니다.** 아래 두 경우다:
 * - 그 축의 채널이 없는 데이터셋(`action/` 에 `done` 만 있는 직접교시 계열이 prod 에피소드의 16.5%)
 * - 아직 집계 전
 */
export type EpisodeBandDeviation = {
  action_outside_fraction: number | null;
  observation_outside_fraction: number | null;
  // `<topic>::<name>` 전체 이름. 예) `action::hand_left`
  action_worst_channel: string | null;
  action_worst_fraction: number | null;
  observation_worst_channel: string | null;
  observation_worst_fraction: number | null;
};

export type EpisodeFileType =
  | "timeseries"
  | "video"
  | "image"
  | "mask"
  | "vector"
  | "metadata"
  | "other";

// 텍스트 네 값(`txt`·`md`·`log`·`jsonl`)은 BE `FileFormat` enum 확장(ZERG-468)을 **미리 반영**한 값이다.
// 서버는 아직 이 값을 보내지 않는다. 그래서 그런 파일은 `other` 로 온다. 이 타입과 미리보기
// 매핑에 미리 두면, BE 가 이 값을 켜는 날 FE 배포 없이 파일이 열린다. `processing` 축도 같은
// 관례를 따른다(docs/rules.md §16).
export type EpisodeFileFormat =
  | "parquet"
  | "mp4"
  | "json"
  | "png"
  | "jpg"
  | "npy"
  | "npz"
  | "hdf5"
  | "yaml"
  | "csv"
  | "txt"
  | "md"
  | "log"
  | "jsonl"
  | "unknown"
  | "other";

export type EpisodeFileResp = {
  uuid: string;
  episode_uuid: string;
  // 소속 에피소드의 visibility 다. 데이터셋 전역 파일 목록에서 excluded 를 걸러낼 때 쓴다.
  // 에피소드 스코프 목록에서는 소속이 분명하므로 쓰지 않는다.
  episode_status: EpisodeStatus;
  directory_uuid: string | null;
  // 부모 디렉터리의 카메라별 channel 이다("wrist_left" 등). camera subdir 가 아니거나 orphan 이면 null 이다.
  directory_channel: string | null;
  // source root 기준으로 파일명까지 포함한 전체 경로다("observation_image/wrist_left/ep_000069.parquet").
  // orphan(directory 없음)이면 null 이다. 파일 단위 다운로드는 이 값으로 디렉터리 트리를 복원한다.
  // signed URL 로 받으면 파일 이름이 object key 의 basename 이 된다. 그러면 카메라별로 이름이 같은
  // 파일이 서로 덮어쓴다.
  relative_path: string | null;
  filename: string;
  file_type: EpisodeFileType;
  file_format: EpisodeFileFormat;
  size_bytes: number | null;
  content_hash: string | null;
  type_details: JsonObject;
  created_at: string;
  modified_at: string;
  // 원본 source 파일의 1회용 presigned GET URL 이다(요청 시점에 서명, video 재생 <video src> 용).
  // 조건을 충족하지 않으면(placement 가 present 가 아님 / orphan) null 이다. parquet 시각화는
  // 이 파일이 아니라 에피소드 진단(diagnostics)의 위상 그리드 signed URL 로 따로 로드한다.
  original_url: string | null;
  original_url_expires_at: string | null;
};

export type EpisodeFileKind = "parquet" | "video" | "other";

// upload finalize 가 parquet 푸터에서 읽어 `EpisodeResp.metadata["parquet"]` 아래 저장하는 구조
// 사실이다. BE 는 이 값을 자동 품질 판정의 입력으로 쓴다. 그래서 판정 결과가 아니라 사실만 담는다.
// 자유 형식 JSONB 라서 namespace 아래에 있다. 이 타입은 미리보기 헤더가 쓰는 두 축만 꺼낸다.
// 구조 지문(`schema_signature`)은 에피소드 간 비교의 입력이라서 단건 표시에는 쓰지 않는다.
export type EpisodeParquetSummary = {
  // 값 컬럼을 가진 채널들의 행 수 중 최솟값이다. 푸터를 읽지 못했으면 null 이다.
  row_count: number | null;
  // 읽지 못한 파일 수다. 0 이 아니면 `row_count` 는 일부 파일에서만 계산한 값이다.
  failed_files: number;
};

export type EpisodeSummary = {
  validation_status: EpisodeValidationStatus;
  // 무효 사유다. 목록 행은 목록 응답에서 옮겨 담는다(`toEpisodeSummary`). 사유가 없으면 null 이다.
  invalid_reason?: EpisodeInvalidReason | null;
  accepted_invalid: boolean;
  // 목록은 스냅샷을 계산하지 않는다. 상세 조회 전에는 필드가 없고, 기록이 없으면 null이다.
  accepted_invalid_snapshot?: EpisodeSnapshot | null;
  accepted_invalid_stale?: boolean | null;
  review_comment_snapshot?: EpisodeSnapshot | null;
  review_comment_stale?: boolean | null;
  uuid: string;
  dataset_uuid: string;
  episode_index: number;
  display_name: string | null;
  duration_seconds: number | null;
  created_at: string;
  status: EpisodeStatus;
  task_failed: boolean;
  quality_tags: readonly string[];
  terms: readonly EpisodeTerm[];
  review_comment: string | null;
  reviewed_by_member: MemberRef | null;
  reviewed_at: string | null;
  annotation_version: string;
  // parquet 구조 사실이다. 이 계약 이전에 적재한 에피소드는 백필하지 않아서 null 이다.
  parquet: EpisodeParquetSummary | null;
  // 이 데이터셋 안에서의 밴드 이탈이다. 목록은 이 값을 행 오른쪽에 색으로 표시한다. 진단 전이면
  // null 이다. 밴드를 담지 않는 경로가 만든 뷰에는 필드가 없다. 두 경우 모두 "측정할 수 없음"으로
  // 표시한다.
  band?: EpisodeBandDeviation | null;
};

// 뷰 투영이다. 에피소드 인스펙터가 쓰는 축만 담는다. 응답의 episode_status·relative_path 는
// 이 뷰를 쓰는 곳이 없어서 담지 않는다(파일 단위 다운로드 화면이 추가되면 필요해질 수 있다).
export type EpisodeFile = {
  uuid: string;
  episode_uuid: string;
  kind: EpisodeFileKind;
  camera_name: string | null;
  filename: string;
  size_bytes: number | null;
  // 목록 응답에 인라인된 원본 1회용 presigned GET URL 이다(없으면 null). video 재생에 쓴다.
  original_url: string | null;
  original_url_expires_at: string | null;
  // video 파일의 선언 fps 다(`type_details.fps`). video 가 아니거나 쓸 수 없는 값이면 null 이다.
  // import 가 meta/info.json 또는 mp4 probe 에서 채운다. 둘 다 실패하면 리뷰의 fps 지정이
  // 채운다. 그래서 오래된 데이터셋은 아직 null 일 수 있다.
  fps: number | null;
};

/**
 * 결함 태그 축의 존재 토큰이다. 값 대신 "그 축의 태그가 하나라도 있는 것"(`any`)과
 * "하나도 없는 것"(`none`)을 고른다. 조회 어휘라서 서버가 값 어휘와 겹치지 않게 막는다.
 * 에피소드 목록(`quality_tag`)과 데이터셋 목록(`episode_quality`)이 같은
 * 두 토큰을 쓴다.
 */
export const EPISODE_TAG_ANY = "any";
export const EPISODE_TAG_NONE = "none";

// dataset 스코프 에피소드 목록 필터다. 모든 필드는 선택이다. 생략하면 제외 에피소드를 포함한
// 전체가 온다. 축 사이는 AND, 한 축 안의 여러 값은 OR 다. 축 이름은 서버 query 파라미터와 1:1 이다.
// 여러 값을 받는 태그 축은 배열로 보관한다. `toEpisodeListParams` 가 배열을 comma 로 합쳐 보낸다.
export type EpisodeListFilters = {
  // 검토 여부다.
  review_state?: EpisodeReviewState;
  // visibility status. 예) `excluded`
  status?: EpisodeStatus;
  // 자동 검증 결과다.
  validation_status?: EpisodeValidationStatus;
  // 자동 태깅한 invalid 사유다. 예) `missing_channel`. 서버는 제외·복원과 관계없이 사유로 거른다.
  invalid_reason?: EpisodeInvalidReason;
  accepted_invalid?: boolean;
  // 실패 여부다. `false` 에는 검토하지 않은 기본 성공도 들어간다.
  task_failed?: boolean;
  // 결함 값 또는 존재 토큰(`any`/`none`)이다. 예) `["collision", "manipulation_failed"]`. scope 밖 값은
  // 422 `episode.invalid_quality_tag` 다.
  quality_tag?: readonly string[];
  // 결함 **갈래** code 다(`/v1/label-categories?key=quality`). 갈래를 고르면 그 갈래의 값을 모두
  // 고른 것과 같다. `quality_tag` 와 **한 축**이라서 함께 보내면 합집합이다. 없는 code 는 422
  // `episode.invalid_quality_category` 다.
  quality_category?: readonly string[];
  // 녹화된 행동 출처다. teleop·inference·intervention·script·none을 OR로 묶는다.
  action_source?: readonly string[];
  // 품질 후보 상태 하나다. 진단이 계산한 결함 후보(지금은 영상 정지)가 있는지로 고른다. 결함 태그
  // 축(`quality_tag`)과 다른 축이다. 후보는 제안일 뿐이고 태그와 연결되지 않는다.
  quality_candidate?: EpisodeQualityCandidate;
  // 영상 정지 최장 후보 길이(초). min 포함, max 미포함이며 후보 상태와 독립이다.
  freeze_longest_min?: number;
  freeze_longest_max?: number;
  // 에피소드 길이(초)다. **반열림 `min <= x < max`**. 길이를 모르는 에피소드는 어느 조건도
  // 통과하지 못한다. facets 의 길이 칸 하나는 `min=칸.min&max=칸.max`(마지막 칸은 min 만)와 같다.
  duration_min?: number;
  duration_max?: number;
  // 에피소드 이름 부분 일치다(대소문자 무시). `%`·`_` 는 서버가 리터럴로 escape 한다.
  search?: string;
  // 축별 밴드 이탈 구간이다. **반열림 `min <= x < max`**. 두 축은 독립이다. 함께 적용하면 AND 다.
  // **값이 없는 에피소드는 어느 구간도 통과하지 못한다.** "측정할 수 없음"은 0% 가 아니다.
  band_action_min?: number;
  band_action_max?: number;
  band_observation_min?: number;
  band_observation_max?: number;
  // 어느 축에서든 이 채널이 가장 많이 벗어난 에피소드만 고른다. 예) `action::hand_left`
  band_worst_channel?: string;
  // 정렬 축과 방향이다. 기본은 디렉터리 순(`sequence_index` 오름차순)이다. 밴드 축으로 정렬하면
  // 서버가 조기 종료를 할 수 없어 비용이 크다. 그래서 사용자가 고를 때만 이 값을 보낸다.
  sort?: EpisodeListSort;
  order?: EpisodeListOrder;
};

// 서버가 소유하는 정렬 어휘다(`service/episode/sort.py`). 서버는 자유 컬럼명을 받지 않는다.
export type EpisodeListSort = "sequence_index" | "band_action" | "band_observation";
export type EpisodeListOrder = "asc" | "desc";

// 서버가 소유하는 축 어휘다. FE 는 값을 하드코딩하지 않고 이 타입으로만 좁힌다.
export type EpisodeReviewState = "reviewed" | "unreviewed";
// `pending` 은 진단이 아직 판정하지 않은 에피소드다. 반입은 결손이 없는 에피소드를 이 값으로 둔다.

// 품질 후보 상태다. `pending` 은 아직 확인하지 않은 후보 구간이 있다. `none` 은 분석이 끝났고
// 후보가 없거나 모두 확인했다. `unanalyzed` 는 분석 값이 없다(video 진단 전, 디코드·분석 실패).
export type EpisodeQualityCandidate = "pending" | "none" | "unanalyzed";

// 질의 축 이름 목록이다. 두 곳에 쓴다.
//   - 서버에 보낼 key 의 허용 목록. 서버는 선언하지 않은 query key 를 422 로 거절한다.
//   - 객체를 순서가 고정된 key 로 직렬화하는 기준(소비처의 재조회 판정).
// 정렬도 이 목록에 포함한다. 정렬이 바뀌면 필터와 같이 목록을 다시 조회해야 한다.
export const EPISODE_LIST_FILTER_KEYS = [
  "review_state",
  "status",
  "validation_status",
  "invalid_reason",
  "accepted_invalid",
  "task_failed",
  "quality_tag",
  "quality_category",
  "action_source",
  "quality_candidate",
  "freeze_longest_min",
  "freeze_longest_max",
  "duration_min",
  "duration_max",
  "search",
  "band_action_min",
  "band_action_max",
  "band_observation_min",
  "band_observation_max",
  "band_worst_channel",
  "sort",
  "order",
] as const;

export type DatasetEpisodeListQuery = PageQuery & EpisodeListFilters;

/**
 * 필터를 서버 query 파라미터로 바꾼다. 여러 값을 받는 태그 축은 comma 로 합치고, 빈 배열은 뺀다.
 *
 * 배열을 그대로 전달하면 apiClient 가 `k=a&k=b` 로 키를 반복한다. 서버는 이 축들을 단일 문자열로
 * 선언하므로 **마지막 값 하나만** 읽는다. 그러면 경고 없이 조건이 좁아진다. 목록과 facets 가 모두
 * 이 함수를 거쳐야 두 응답의 술어가 같다.
 *
 * `EPISODE_LIST_FILTER_KEYS` 에 있는 key 만 보낸다. 서버는 모르는 key 를 무시하지 않고 422 로
 * 거절한다. 그래서 필터 객체에 다른 key 가 섞이면 목록 요청 전체가 실패한다.
 */
export function toEpisodeListParams(
  filters: EpisodeListFilters,
): Record<string, string | number | boolean | undefined> {
  const params: Record<string, string | number | boolean | undefined> = {};
  for (const key of EPISODE_LIST_FILTER_KEYS) {
    const value = filters[key];
    if (Array.isArray(value)) {
      if (value.length > 0) {
        params[key] = value.join(",");
      }
    } else if (value !== undefined) {
      params[key] = value as string | number | boolean;
    }
  }
  return params;
}

export async function listDatasetEpisodes(
  datasetUuid: string,
  query: DatasetEpisodeListQuery = {},
): Promise<PageResp<EpisodeSummary>> {
  const { page, size, ...filters } = query;
  const response = await apiClient.get<PageResp<EpisodeResp>>(
    apiRoutes.dataset.episodes(datasetUuid),
    { params: { ...createPageParams({ page, size }), ...toEpisodeListParams(filters) } },
  );
  const resp = response.data;
  return {
    ...resp,
    items: resp.items.map((episode) => toEpisodeSummary(datasetUuid, episode)),
  };
}

// 필터 축의 선택지 하나다. 표시 문구는 담지 않는다. enum 축의 한국어 문구는 FE 가 소유한다.
export type EpisodeFacetCount = {
  value: string;
  count: number;
};

// 결함 갈래 하나다. 이 타입에만 `display_name` 이 있다. 갈래 이름의 SoT 는 DB 이고, 운영자가
// 이름을 고칠 수 있기 때문이다. `code` 는 바뀌지 않는 식별자이고 목록 질의 토큰(`quality_category`)이다.
export type EpisodeQualityCategoryFacet = {
  uuid: string;
  code: string;
  display_name: string;
  count: number;
};

// 결함 축이다. 모든 수는 **distinct 에피소드 수**다. 한 에피소드에 태그가 여러 개일 수 있어서
// 갈래 합 ≥ `total` 이 정상이다. 그래서 비율 분모로 쓰면 100% 를 넘는다.
//   - `total`: 결함이 하나라도 있는 수(`any`)
//   - `none`: 결함이 하나도 없는 수
//   - `tags`: 값별 수(어휘 순 + 어휘 밖 값). 갈래 소속은 담지 않는다. 소속은 `/v1/label-categories` 가 준다.
//   - `uncategorized`: 갈래가 없는 결함 값이 붙은 수
export type EpisodeQualityFacet = {
  total: number;
  none: number;
  uncategorized: number;
  categories: readonly EpisodeQualityCategoryFacet[];
  tags: readonly EpisodeFacetCount[];
};

// 행동 출처 축이다. total은 값이 있는 수, none은 값이 없는 수다.
export type EpisodeActionSourceFacet = {
  total: number;
  none: number;
  values: readonly EpisodeFacetCount[];
};

// 범위 칸 하나다. **반열림 `min <= x < max` 이다. 마지막 칸만 `max` 를 포함한다.**
export type EpisodeRangeBin = {
  min: number;
  max: number;
  count: number;
};

// 길이 축이다. 칸 경계는 **필터 없는 데이터셋**의 최소·최대로 정한다. 그래서 다른 필터가 바뀌어도
// 경계는 바뀌지 않는다. 수만 조건에 따라 바뀐다. `unspecified` 는 길이를 모르는 에피소드 수다
// (범위 필터를 통과하지 않는다). 모두 모르면 `bins` 가 비어 있다. 값이 하나면 칸도 하나다.
export type EpisodeDurationFacet = {
  unspecified: number;
  bins: readonly EpisodeRangeBin[];
};

// 밴드 한 축이다. 0 부터 1 까지 0.05 폭의 20칸이다. 서버는 임계값을 가지지 않는다. 화면이 자기
// 임계값으로 칸을 합친다. `unmeasured` 는 그 축 값이 없는 에피소드 수다(진단 전·그 축 채널 없음).
export type EpisodeBandAxisFacet = {
  unmeasured: number;
  bins: readonly EpisodeRangeBin[];
};

// 최악 채널 하나다. 두 축 최악 채널의 합집합을 채널별 distinct 에피소드로 센다.
export type EpisodeWorstChannelFacet = {
  channel: string;
  count: number;
};

export type EpisodeBandFacet = {
  action: EpisodeBandAxisFacet;
  observation: EpisodeBandAxisFacet;
  // 개수 내림차순, 그다음 이름순이다.
  worst_channels: readonly EpisodeWorstChannelFacet[];
};

// 축별 선택지 개수의 한 세트다. 값은 **조건부**다. 각 축의 선택지 수는 **그 축만 뺀** 나머지 활성
// 필터를 적용한 수다. 그래서 그 축만 그 값으로 바꾼 같은 질의의 목록 `total` 과 같다. `total` 은
// 모든 필터를 적용한 목록 total 이다. 값이 0 인 선택지도 모두 포함한다. enum 축은 서버가
// enum 전체를 담는다. 화면이 표시하는 선택지는 그보다 적다. 그래서 소비처는 이 값을 옵션 목록으로
// 쓰지 않는다. **값으로 조인해 개수만** 가져다 쓴다.
export type EpisodeFacets = {
  total: number;
  review_state: readonly EpisodeFacetCount[];
  status: readonly EpisodeFacetCount[];
  validation_status: readonly EpisodeFacetCount[];
  // invalid 사유별 수다. 사유가 없는 invalid 는 어느 선택지에도 들지 않는다.
  invalid_reason: readonly EpisodeFacetCount[];
  accepted_invalid: readonly EpisodeFacetCount[];
  // 값은 `"false"`·`"true"` 두 문자열이다.
  task_failed: readonly EpisodeFacetCount[];
  quality: EpisodeQualityFacet;
  // 품질 후보 세 상태의 수다. 세 수의 합은 `total` 이다.
  quality_candidate: readonly EpisodeFacetCount[];
  // @compat ZERG-937 until=be:c22b7741
  freeze_longest?: {
    no_candidate: number;
    bins: readonly { min: number; max: number | null; count: number }[];
  };
  action_source: EpisodeActionSourceFacet;
  duration: EpisodeDurationFacet;
  band: EpisodeBandFacet;
};

/**
 * 데이터셋 하나의 필터 축별 개수를 조회한다. 목록과 **같은 필터**를 받는다.
 *
 * 정렬(`sort`·`order`)과 페이지는 보내지 않는다. 서버 facets 는 그 key 를 선언하지 않아서 422 로 거절한다.
 *
 * 필터가 바뀌면 수도 바뀐다. 결과를 캐시하지 않는다. 값은 필터 조합마다 다르고 리뷰마다 바뀐다.
 * 다시 조회할 시점은 소비 화면이 정한다(수가 표시되는 동안만).
 *
 * 응답을 투영하지 않고 그대로 반환한다. 목록(`EpisodeResp` → `EpisodeSummary`)과 달리 응답이
 * 이미 화면이 쓰는 모양이다. 중간 층을 두면 같은 필드가 두 번 정의된다.
 */
export async function getDatasetEpisodeFacets(
  datasetUuid: string,
  filters: EpisodeListFilters = {},
): Promise<EpisodeFacets> {
  const rest = { ...filters };
  delete rest.sort;
  delete rest.order;
  const response = await apiClient.get<EpisodeFacets>(
    apiRoutes.dataset.episodeFacets(datasetUuid),
    { params: toEpisodeListParams(rest) },
  );
  return response.data;
}

// 단건 에피소드를 조회한다. annotation 충돌(409) 후 활성 행 하나만 최신값으로 다시 조회한다.
// 응답은 목록 항목과 같은 flat EpisodeResp 라서 같은 projection 을 재사용한다. 그래서 목록 전체를
// 다시 가져오지 않는다(스크롤·누적 목록 보존). 404(에피소드/데이터셋 부재·visibility)는 그대로 던진다.
export async function getDatasetEpisodeReview(
  datasetUuid: string,
  episodeUuid: string,
): Promise<EpisodeSummary> {
  const response = await apiClient.get<EpisodeResp>(
    apiRoutes.dataset.episode(datasetUuid, episodeUuid),
  );
  return toEpisodeSummary(datasetUuid, response.data);
}

// 데이터셋 canonical 파일 목록의 요청이다. 서버는 이 목록의 page 를 cursor 로 나눈다.
//   - after: 호출자는 앞 응답의 cursor 를 그대로 보낸다. 첫 요청에는 보내지 않는다.
//   - size: 1~1,000 이다. 서버 기본값은 100 이다.
//   - 호출자는 다음 요청에서 after 만 바꾼다. 다른 값은 첫 요청과 같게 보낸다.
// 서버는 선언하지 않은 query key 를 422 로 거부한다. page 도 그 key 다.
// with_download_url=true 면 서버가 행마다 signed GET 을 인라인한다. 서명은 행마다 비용이 든다.
// 그래서 실제로 다운로드할 때만 켠다(기본 false).
// parent 를 보내면 그 디렉터리 직속 파일만 온다(recursive=true 면 하위까지).
export type DatasetEpisodeFileListQuery = {
  after?: string;
  size?: number;
  parent?: string;
  recursive?: boolean;
  with_download_url?: boolean;
};

// 데이터셋 canonical 파일 목록의 page 다. cursor 는 서버가 만든 문자열이다. 호출자는 값을
// 해석하지 않는다. cursor 가 null 이면 이 page 가 마지막이다.
export type DatasetEpisodeFilePage = {
  items: EpisodeFileResp[];
  cursor: string | null;
};

// 단건 canonical 파일의 1회용 signed GET URL 이다.
export type EpisodeFileDownloadUrl = {
  url: string;
  method: HttpMethod;
  expires_at: string;
};

/**
 * 데이터셋 전역 canonical 파일 page 를 조회한다. 파일 단위 다운로드는 이 함수로 대상 목록을 만든다.
 *
 * 에피소드 스코프 `listEpisodeFiles` 와 달리 뷰 투영(`EpisodeFile`)을 거치지 않는다. raw 응답을
 * 그대로 반환한다. 다운로드는 `relative_path`(디렉터리 복원) · `episode_status`(excluded 판정) ·
 * `size_bytes`(재개 판정)를 쓴다. 투영은 영상 인스펙터 축만 담으므로 필요한 축이 겹치지 않는다.
 *
 * excluded 에피소드의 파일도 함께 온다. 그래서 다운로드할 파일은 호출자가 고른다.
 *
 * 응답에 `cursor` key 가 없으면 이 함수는 오류를 던진다. 호출자는 `cursor` 로 마지막 page 를
 * 판정한다. 호출자가 key 가 없는 응답을 마지막 page 로 읽으면 목록의 앞부분만 받는다.
 */
export async function listDatasetEpisodeFiles(
  datasetUuid: string,
  query: DatasetEpisodeFileListQuery = {},
  options: { signal?: AbortSignal } = {},
): Promise<DatasetEpisodeFilePage> {
  const response = await apiClient.get<DatasetEpisodeFilePage>(
    apiRoutes.dataset.allEpisodeFiles(datasetUuid),
    { params: query, signal: options.signal },
  );
  if (response.data.cursor === undefined) {
    throw new Error("episode-files response has no cursor key");
  }
  return response.data;
}

// 단건 canonical 파일을 다시 서명한다. 목록 URL 이 만료되었을 때 그 파일의 URL 만 다시 받는다.
// 에러:
//   - 404 dataset.not_found
//   - 404 episode_file.not_found. 파일이 이 dataset 에 매핑되지 않아도 이 코드다(존재 노출 방지).
//   - 409 download.orphan_file(directory NULL)
//   - 503 dataset.storage_backend_not_configured(서버에 이 Dataset 의 저장 위치 설정이 없음)
export async function getDatasetEpisodeFileDownloadUrl(
  datasetUuid: string,
  episodeFileUuid: string,
): Promise<EpisodeFileDownloadUrl> {
  const response = await apiClient.get<EpisodeFileDownloadUrl>(
    apiRoutes.dataset.episodeFileDownloadUrl(datasetUuid, episodeFileUuid),
  );
  return response.data;
}

export async function listEpisodeFiles(
  datasetUuid: string,
  episodeUuid: string,
  query: PageQuery = {},
): Promise<EpisodeFile[]> {
  const response = await apiClient.get<PageResp<EpisodeFileResp>>(
    apiRoutes.dataset.episodeFiles(datasetUuid, episodeUuid),
    {
      params: {
        page: query.page ?? 1,
        size: query.size ?? DEFAULT_EPISODE_FILE_PAGE_SIZE,
      },
    },
  );
  return response.data.items.map(toEpisodeFile);
}

function createPageParams(query: PageQuery): PageQuery {
  return {
    page: query.page,
    size: query.size,
  };
}

function toEpisodeSummary(datasetUuid: string, episode: EpisodeResp): EpisodeSummary {
  return {
    validation_status: episode.validation_status,
    invalid_reason: episode.invalid_reason,
    accepted_invalid: episode.accepted_invalid,
    accepted_invalid_snapshot: episode.accepted_invalid_snapshot,
    accepted_invalid_stale: episode.accepted_invalid_stale,
    review_comment_snapshot: episode.review_comment_snapshot,
    review_comment_stale: episode.review_comment_stale,
    uuid: episode.uuid,
    dataset_uuid: datasetUuid,
    episode_index: episode.sequence_index,
    display_name: episode.name,
    duration_seconds: episode.duration_seconds,
    created_at: episode.created_at,
    status: episode.status,
    task_failed: episode.task_failed,
    quality_tags: episode.quality_tags ?? [],
    terms: episode.terms ?? [],
    review_comment: episode.review_comment ?? null,
    reviewed_by_member: episode.reviewed_by_member ?? null,
    reviewed_at: episode.reviewed_at ?? null,
    annotation_version: episode.annotation_version,
    parquet: toEpisodeParquetSummary(episode.metadata),
    band: episode.band ?? null,
  };
}

// `metadata` 는 여러 writer 가 각자 namespace 를 쓰는 자유 형식 JSONB 다. 그래서 모양을 신뢰하지
// 않고 축별로 좁힌다. namespace 가 없거나(계약 이전 에피소드) 모양이 맞지 않으면 null 을 반환한다.
// 소비자는 그 자리를 비워 둔다.
const EPISODE_PARQUET_METADATA_KEY = "parquet";

function toEpisodeParquetSummary(metadata: JsonObject): EpisodeParquetSummary | null {
  const raw = metadata[EPISODE_PARQUET_METADATA_KEY];
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) {
    return null;
  }
  const summary = raw as JsonObject;
  return {
    row_count: readFiniteNumber(summary.row_count),
    failed_files: readFiniteNumber(summary.failed_files) ?? 0,
  };
}

function readFiniteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function toEpisodeFile(file: EpisodeFileResp): EpisodeFile {
  const kind = resolveEpisodeFileKind(file);
  return {
    uuid: file.uuid,
    episode_uuid: file.episode_uuid,
    kind,
    camera_name: kind === "video" ? resolveCameraName(file) : null,
    filename: file.filename,
    size_bytes: file.size_bytes,
    original_url: file.original_url,
    original_url_expires_at: file.original_url_expires_at,
    fps: kind === "video" ? readVideoFps(file.type_details) : null,
  };
}

/**
 * video `type_details.fps` 를 읽는다. 서버 `service/episode/schema.video_fps_value` 와 같은 규칙이다.
 *
 * `type_details` 는 자유 형식 JSONB 라서 모양을 신뢰하지 않는다. 서버는 0 을 "모름"으로 기록한다.
 * 리뷰의 fps 지정도 `> 0` 인 행만 값이 있다고 본다. 그래서 여기서도 0 은 없음(null)으로 바꾼다.
 * 소비처가 0 으로 나누면 안 되기 때문이다.
 */
function readVideoFps(typeDetails: JsonObject): number | null {
  const fps = readFiniteNumber(typeDetails["fps"]);
  return fps !== null && fps > 0 ? fps : null;
}

function resolveEpisodeFileKind(file: EpisodeFileResp): EpisodeFileKind {
  if (file.file_type === "video") {
    return "video";
  }
  if (file.file_format === "parquet") {
    return "parquet";
  }
  return "other";
}

// 파일명 stem 이 에피소드 이름과 같은지 판정한다. LeRobot 계열은 카메라 영상도
// episode_000000.mp4 로 저장하고, 카메라 식별은 경로에 둔다. 이런 stem 은 카메라 이름이 아니므로 버린다.
const EPISODE_NAME_PATTERN = /^episode[_-]?\d+$/i;

function resolveCameraName(file: EpisodeFileResp): string | null {
  // 카메라 채널명의 정본은 부모 디렉터리다(카메라별 subdir, 예: "wrist_left"). import 가 경로에서
  // 이 값을 추출해 directory_channel 로 준다. video 파일명은 episode 이름이라서 카메라를 구분하지
  // 못한다. 그래서 이 값을 카메라 식별자로 먼저 쓴다.
  const directoryChannel = readString(file.directory_channel);
  if (directoryChannel !== null) {
    return directoryChannel;
  }

  // 파일명 stem 을 카메라 이름 폴백으로 쓴다(예: left_cam.mp4 → left_cam). 단 stem 이
  // episode_000000 처럼 에피소드 이름이면 카메라 식별값이 아니므로 버린다. 그래서 플레이어 캡션에
  // 에피소드 이름이 표시되지 않는다(episodeChannels 가 "Camera N" 으로 폴백한다).
  const extensionIndex = file.filename.lastIndexOf(".");
  const stem = extensionIndex > 0 ? file.filename.slice(0, extensionIndex) : file.filename;
  if (stem.trim() === "" || EPISODE_NAME_PATTERN.test(stem.trim())) {
    return null;
  }
  return stem;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() !== "" ? value : null;
}
